import React from 'react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { AlertTriangle, Fuel, Banknote, Loader2 } from 'lucide-react';

const fmt = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;
const GROUPS = [
  { key: 'fuel', title: 'Fuel Variance', icon: Fuel },
  { key: 'cash', title: 'Cash Variance', icon: Banknote },
];
const MIN_COMMENT = 5;

const CheckRow = ({ c }) => (
  <div className="p-3 rounded-md bg-background/60 border border-amber-500/20 space-y-1" data-testid={`variance-check-${c.key}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-semibold">{c.label}</span>
      <span className="font-mono font-bold text-red-500">{fmt(c.variance)}</span>
    </div>
    <p className="text-sm text-muted-foreground">{c.explanation}</p>
    <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
      <span>{c.enteredLabel}: <span className="font-mono">{fmt(c.entered)}</span></span>
      <span>{c.expectedLabel}: <span className="font-mono">{fmt(c.expected)}</span></span>
      <span>Allowed: <span className="font-mono">±{fmt(c.limit)}</span></span>
    </div>
  </div>
);

export const VarianceApprovalDialog = ({ open, onOpenChange, checks = [], adminMode = false, requesting, comment = '', onCommentChange, onEditData, onRequestApproval }) => {
  if (!checks.length) return null;
  const commentOk = adminMode || comment.trim().length >= MIN_COMMENT;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="variance-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            Variance Warning
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {GROUPS.map(g => {
            const items = checks.filter(c => c.group === g.key);
            if (!items.length) return null;
            const Icon = g.icon;
            return (
              <div key={g.key} className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg space-y-2" data-testid={`variance-group-${g.key}`}>
                <p className="flex items-center gap-2 font-semibold text-amber-600 text-lg">
                  <Icon className="h-5 w-5" />{g.title}
                </p>
                {items.map(c => <CheckRow key={c.key} c={c} />)}
              </div>
            );
          })}
          {!adminMode && (
            <div className="space-y-1">
              <Label htmlFor="variance-comment">Comments / Explanation <span className="text-red-500">*</span></Label>
              <Textarea
                id="variance-comment"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="Explain why there is a variance (e.g. cashier entry error, pump test, card terminal issue)"
                rows={3}
                data-testid="variance-comment-input"
              />
              {!commentOk && <p className="text-xs text-muted-foreground">Please enter an explanation to request approval.</p>}
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            {adminMode
              ? 'As an admin you can submit with these variances - no approval request is needed.'
              : 'Requesting admin approval sends ONE request with all variances above and locks this entry until an admin reviews it.'}
          </p>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
          <Button variant="outline" onClick={onEditData} className="w-full sm:w-auto" data-testid="variance-edit-data-btn">
            Check and Edit Data
          </Button>
          <Button
            onClick={onRequestApproval}
            disabled={requesting || !commentOk}
            data-testid="variance-request-approval-btn"
            className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white"
          >
            {requesting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>)
              : adminMode ? 'Acknowledge & Submit' : 'Request Admin Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
